import { ActivitySpinnerSize, ActivitySpinnerWidget, ButtonType, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogProps, DialogType, DialogUserResizeMode, DialogWidget, IntegerStepperWidget, TabStyles, TextLinkWidget } from '@tableau/widgets';

import { CommonStyles } from '../../../common/react/CommonStyles';

import React = require('react');
import { Messages } from '../../../codegen/Localize';
import { ExpandUserQuotaRequest, UserQuotaIncrease } from '../../../ts/ServerApi/requests/ExpandUserQuotaRequest';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { SessionManagementService } from '../../../sessionManagement/SessionManagementService';
import { ToasterService, ToastType } from '../../../ts/services/ToasterService';
import WebHttpClient from '../../../httpClient/WebHttpClient';

const pricingUrl = 'https://www.tableau.com/pricing/teams-orgs';

interface BuyModalProps {
    onClose: () => void;
    server: SessionManagementService;
    reportTelemetry: (orderQuantities: UserQuotaIncrease) => void;
}

interface BuyModalState {
    readonly orderQuantities: UserQuotaIncrease;
    readonly isSubmitting: boolean;
    readonly isValid: boolean;
}

export class BuyModal extends React.Component<BuyModalProps, BuyModalState>  {
    private toaster: ToasterService;

    public constructor(props: BuyModalProps) {
        super(props);
        const orderQuantities: UserQuotaIncrease = {
            creators: 0,
            viewers: 0,
            explorers: 0
        };
        this.state = {orderQuantities: orderQuantities, isSubmitting: false, isValid: false};
        this.toaster = ToasterService.instance;
    }

    private submitButtonMessageOrSpinner = (): React.ReactNode => {
        return this.state.isSubmitting ?
        <div style={{ marginTop: TabStyles.Spacing.SmallSpace! }}>
                  <ActivitySpinnerWidget spinnerSize={ActivitySpinnerSize.Small} testId="submitting" />
                </div>
                 : Messages.buyLicenseDialogSubmit();
    }

    private footerContent = (): React.ReactNode => {
        return <DialogFooterButtonsWidget
                buttons={[
                    {
                        handleClick: () => {
                            this.props.onClose();
                        },
                        buttonType: ButtonType.Outline,
                        children: Messages.cancel(),
                        testId: 'buy-license-dialog-footer-cancel'
                    },
                    {
                        handleClick: this.submit,
                        buttonType: ButtonType.Go,
                        disabled: this.state.isSubmitting || !this.state.isValid,
                        children: this.submitButtonMessageOrSpinner(),
                        testId: 'buy-license-dialog-footer-confirm'
                    }
                ]}
                testId="buy-license-dialog-footer"
            />;
    }

    private dialogProps = (): DialogProps => {
        return {
            shouldReturnFocusOnClose: false,
            dialogWidth: 600,
            testId: 'buy-license-dialog',
            type: DialogType.NormalWithTitleBar,
            title: Messages.buyLicenses(),
            positioningBehavior: DialogPositioningBehavior.Draggable,
            isModal: true,
            showCloseButton: true,
            onCloseRequest: this.props.onClose,
            showFooter: true,
            footerContent: this.footerContent(),
            userResizeMode: DialogUserResizeMode.NotResizable,
        };
    }

    private updateOrder = (role: string, value: number): void => {
        this.setState({ orderQuantities: { ...this.state.orderQuantities, [role]: value} }, () => {
            //validate once state has been set
            this.validateOrder();
         });
    }

    private validateOrder = (): void => {
        if(this.state.orderQuantities.creators +
           this.state.orderQuantities.explorers +
           this.state.orderQuantities.viewers > 0)
        {
            this.setState({isValid: true});
        }
        else
        {
            this.setState({isValid: false});
        }
    }

    private submit = (): void => {
        this.setState({isSubmitting: true});
        WebHttpClient.sendRequest(new ExpandUserQuotaRequest(this.state.orderQuantities)).
        then((result: IResult) => {
            if(result && result.errors && result.errors.length > 0) {
                this.toaster.pop({ type: ToastType.Error, text: Messages.buyLicenseDialogError() });
            }
            else{
                this.toaster.pop({ type: ToastType.Info, text: Messages.buyLicenseDialogSuccess() });
            }
            this.props.reportTelemetry(this.state.orderQuantities);
            this.setState({isSubmitting: false});
            this.props.onClose();
        });
    }

    public render(): JSX.Element {
        return <DialogWidget {...this.dialogProps()}>
            <div style={textStyle}>{Messages.buyLicenseDialog1()}</div>
            <div style={textStyleBold}>{Messages.buyLicenseDialog2()}<TextLinkWidget href={pricingUrl} testId={'buy-license-pricing-link'}>{Messages.buyLicenseDialogLink()}</TextLinkWidget></div>
            <div style={containerStyle}>
                <RoleStepper
                    roleName={Messages.licenseCreator()}
                    roleDescription={Messages.licenseCreatorDescription()}
                    testId="buy-license-creator-stepper"
                    onValueUpdated={(value: number) => {
                        this.updateOrder('creators', value);
                    }}
                />
                <RoleStepper
                    roleName={Messages.licenseExplorers()}
                    roleDescription={Messages.licenseExplorersDescription()}
                    testId="buy-license-explorer-stepper"
                    onValueUpdated={(value: number) => {
                        this.updateOrder('explorers', value);
                    }}
                />
                <RoleStepper
                    roleName={Messages.licenseViewer()}
                    roleDescription={Messages.licenseViewerDescription()}
                    testId="buy-license-viewer-stepper"
                    onValueUpdated={(value: number) => {
                        this.updateOrder('viewers', value);
                    }}
                />
                <div style={textStyleBold}>{Messages.buyLicenseDialog3()}</div>
            </div>
    </DialogWidget>;
    }

}

interface RoleStepperProps {
    onValueUpdated: (value: number) => void;
    testId: string;
    roleName: string;
    roleDescription: string;
}

const RoleStepper: React.FC<RoleStepperProps> = (props: RoleStepperProps) => {
    const integerStepperCommonProps = {
        max: 9999,
        min: 0,
        start: 0,
        inputStyle: { minWidth: 75, maxWidth: 75 },
        onValueUpdated: props.onValueUpdated,

    };
    return <>
        <div>
            <div style={textStyleBold}>{props.roleName}</div>
            <div style={textStyle}>{props.roleDescription}</div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{
            margin: 'auto',
        }}>
            <IntegerStepperWidget {...integerStepperCommonProps} testId={props.testId} />
        </div>
    </>;
};

const containerStyle: React.CSSProperties = { display: 'flex', flexDirection: 'row', flexWrap: 'wrap' };

const textStyle: React.CSSProperties = {
    marginTop: CommonStyles.Sizes.MediumSpace,
    marginBottom: CommonStyles.Sizes.MediumSpace
};

const textStyleBold: React.CSSProperties = {
    ...textStyle,
    fontWeight: 'bold',
};
